const MINOR_UNITS_PER_MAJOR = 100;

export const MAX_SERVICE_PRICE_MAJOR = 999_999.99;

export function majorToMinorUnits(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === "") return null;

  if (typeof value === "number" && !Number.isFinite(value)) {
    throw new Error("Money value must be finite");
  }

  const normalized = String(Object.is(value, -0) ? 0 : value)
    .trim()
    .replace(",", ".");
  const match = normalized.match(/^(\d+)(?:\.(\d{1,2}))?$/);

  if (!match) {
    throw new Error("Money value must have at most two decimal places");
  }

  const wholeUnits = Number(match[1]);
  const minorUnits = Number((match[2] || "").padEnd(2, "0"));
  const result = wholeUnits * MINOR_UNITS_PER_MAJOR + minorUnits;

  if (!Number.isSafeInteger(result)) {
    throw new Error("Money value is too large");
  }

  return result;
}

export function minorToMajorUnits(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === "") return null;

  const normalized = typeof value === "string" ? value.trim() : value;

  if (
    (typeof normalized === "string" && !/^\d+$/.test(normalized)) ||
    (typeof normalized === "number" && !Number.isFinite(normalized))
  ) {
    throw new Error("Minor-unit money value must be a non-negative integer");
  }

  const minorUnits = Number(normalized);

  if (!Number.isSafeInteger(minorUnits) || minorUnits < 0) {
    throw new Error("Minor-unit money value must be a non-negative integer");
  }

  const wholeUnits = Math.floor(minorUnits / MINOR_UNITS_PER_MAJOR);
  const fraction = String(minorUnits % MINOR_UNITS_PER_MAJOR).padStart(2, "0");

  return Number(`${wholeUnits}.${fraction}`);
}

export function formatMoneyAmount(
  value: number | null | undefined,
  currency = "EUR",
  locale = "lt-LT",
) {
  if (value == null || !Number.isFinite(value)) return "—";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}
